export type Level =
  | 'Individual Contributor'
  | 'Team Lead'
  | 'Manager'
  | 'Director'
  | 'VP'
  | 'Executive'

export type Competency = {
  id: number
  name: string
  description: string
  category: string
  levels: Level[]
  tags: string[]
  questions: number
  archived: boolean
}

/** Fixed, ordered seniority ladder. Order here drives display order everywhere. */
export const LEVELS: Level[] = [
  'Individual Contributor',
  'Team Lead',
  'Manager',
  'Director',
  'VP',
  'Executive',
]

/** Compact labels for tight spaces such as card chips. */
export const LEVEL_SHORT: Record<Level, string> = {
  'Individual Contributor': 'IC',
  'Team Lead': 'Team Lead',
  Manager: 'Manager',
  Director: 'Director',
  VP: 'VP',
  Executive: 'Exec',
}

export const CATEGORIES = [
  'Leadership',
  'Governance',
  'Strategic Thinking',
  'Accountability',
  'Risk',
  'Communication',
  'Emotional Intelligence',
  'Collaboration',
  'Team Development',
]

export const CATEGORY_COLORS: Record<string, string> = {
  Leadership: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  Governance: 'bg-blue-50 text-blue-700 border-blue-100',
  'Strategic Thinking': 'bg-violet-50 text-violet-700 border-violet-100',
  Accountability: 'bg-amber-50 text-amber-700 border-amber-100',
  Risk: 'bg-red-50 text-red-700 border-red-100',
  Communication: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  'Emotional Intelligence': 'bg-pink-50 text-pink-700 border-pink-100',
  Collaboration: 'bg-cyan-50 text-cyan-700 border-cyan-100',
  'Team Development': 'bg-teal-50 text-teal-700 border-teal-100',
}

/**
 * An empty `levels` array means the competency applies to EVERY level.
 * This keeps legacy records valid and prevents anything from silently
 * disappearing behind a level filter.
 */
export function appliesToLevel(competency: Competency, level: Level): boolean {
  if (competency.levels.length === 0) return true
  return competency.levels.includes(level)
}

export function isUniversal(competency: Competency): boolean {
  return competency.levels.length === 0
}

/** Sorts a set of levels into seniority order for consistent display. */
export function sortLevels(levels: Level[]): Level[] {
  return [...levels].sort((a, b) => LEVELS.indexOf(a) - LEVELS.indexOf(b))
}

export const INITIAL_COMPETENCIES: Competency[] = [
  {
    id: 1,
    name: 'Strategic Vision',
    description:
      'Sets a clear, compelling long-term direction and aligns the team around it.',
    category: 'Strategic Thinking',
    levels: ['Director', 'VP', 'Executive'],
    tags: ['vision', 'planning'],
    questions: 6,
    archived: false,
  },
  {
    id: 2,
    name: 'Decision Making',
    description:
      'Makes timely, well-reasoned decisions under ambiguity and pressure.',
    category: 'Leadership',
    levels: ['Manager', 'Director', 'VP', 'Executive'],
    tags: ['judgment', 'execution'],
    questions: 5,
    archived: false,
  },
  {
    id: 3,
    name: 'Board Oversight',
    description:
      'Ensures effective governance structures and accountability to stakeholders.',
    category: 'Governance',
    levels: ['VP', 'Executive'],
    tags: ['compliance', 'oversight'],
    questions: 7,
    archived: false,
  },
  {
    id: 4,
    name: 'Ownership & Follow-through',
    description:
      'Takes responsibility for outcomes and delivers on commitments.',
    category: 'Accountability',
    levels: [],
    tags: ['delivery', 'trust'],
    questions: 4,
    archived: false,
  },
  {
    id: 5,
    name: 'Risk Assessment',
    description:
      'Identifies, evaluates, and mitigates organizational and operational risks.',
    category: 'Risk',
    levels: ['Manager', 'Director', 'VP', 'Executive'],
    tags: ['mitigation', 'analysis'],
    questions: 5,
    archived: false,
  },
  {
    id: 6,
    name: 'Clear Communication',
    description:
      'Conveys ideas and expectations clearly across all levels of the organization.',
    category: 'Communication',
    levels: [],
    tags: ['clarity', 'influence'],
    questions: 6,
    archived: false,
  },
  {
    id: 7,
    name: 'Self-Awareness',
    description: 'Understands own emotions, strengths, and impact on others.',
    category: 'Emotional Intelligence',
    levels: [],
    tags: ['empathy', 'reflection'],
    questions: 5,
    archived: false,
  },
  {
    id: 8,
    name: 'Cross-functional Collaboration',
    description: 'Builds productive partnerships across teams and departments.',
    category: 'Collaboration',
    levels: ['Team Lead', 'Manager', 'Director', 'VP'],
    tags: ['teamwork', 'alignment'],
    questions: 4,
    archived: false,
  },
  {
    id: 9,
    name: 'Coaching & Mentoring',
    description:
      'Develops talent through feedback, coaching, and growth opportunities.',
    category: 'Team Development',
    levels: ['Team Lead', 'Manager', 'Director'],
    tags: ['growth', 'feedback'],
    questions: 6,
    archived: false,
  },
  {
    id: 10,
    name: 'Legacy Influence Model',
    description:
      'Deprecated framework retained for historical assessment comparisons.',
    category: 'Leadership',
    levels: ['Executive'],
    tags: ['legacy'],
    questions: 3,
    archived: true,
  },
  {
    id: 11,
    name: 'Craft Excellence',
    description:
      'Demonstrates deep technical skill and raises the quality bar within their discipline.',
    category: 'Accountability',
    levels: ['Individual Contributor', 'Team Lead'],
    tags: ['quality', 'delivery'],
    questions: 5,
    archived: false,
  },
  {
    id: 12,
    name: 'Adaptability',
    description:
      'Responds constructively to shifting priorities and changing circumstances.',
    category: 'Emotional Intelligence',
    levels: ['Individual Contributor', 'Team Lead', 'Manager'],
    tags: ['resilience', 'growth'],
    questions: 4,
    archived: false,
  },
]
